import type { FormEvent } from 'react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Logo from './Logo';

function readAuthErrorFromHash(): string {
  const hash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : '';
  if (!hash) return '';

  const params = new URLSearchParams(hash);
  if (!params.get('error')) return '';

  const code = params.get('error_code');
  if (code === 'otp_expired' || code === 'access_denied') {
    return 'El enlace de recuperación expiró o ya fue utilizado. Solicita uno nuevo.';
  }
  return params.get('error_description')?.replace(/\+/g, ' ') ?? 'No se pudo validar el enlace.';
}

export default function Login() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const hashError = readAuthErrorFromHash();
    if (hashError) {
      setError(hashError);
      // Limpia el hash para que un refresh no vuelva a mostrar el error.
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }, []);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError('');
    setLoading(true);

    const { error } = await signIn(email, password);

    if (error) {
      setError('Credenciales incorrectas. Verifica tu email y contraseña.');
      setLoading(false);
      return;
    }

    navigate('/', { replace: true });
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-inset">
      <div className="w-full max-w-[360px]">
        {/* Brand */}
        <div className="mb-8 text-center">
          <Logo size={44} className="text-accent mx-auto mb-3" animate />
          <h2 className="font-serif text-[1.6rem] font-semibold text-accent tracking-tight">
            Legal Hermes
          </h2>
          <p className="text-ink-muted text-[0.8125rem] mt-1.5">Monitor de procesos judiciales</p>
        </div>

        {/* Card */}
        <div className="bg-surface-raised rounded-lg p-7 border border-border-emphasis shadow-[0_8px_32px_rgba(0,0,0,0.4)]">
          <form onSubmit={handleSubmit}>
            <div className="mb-5">
              <label
                className="block mb-1.5 font-medium text-[0.8125rem] text-ink-secondary"
                htmlFor="email"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                className="w-full px-3.5 py-2.5 border border-border-emphasis rounded-sm text-[0.875rem] pointer-coarse:text-base font-sans bg-surface-inset text-ink placeholder-ink-muted focus:outline-none focus:border-accent focus:shadow-[0_0_0_3px_rgba(200,150,62,0.1)] transition-[border-color,box-shadow] duration-150"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>

            <div className="mb-6">
              <label
                className="block mb-1.5 font-medium text-[0.8125rem] text-ink-secondary"
                htmlFor="password"
              >
                Contraseña
              </label>
              <input
                id="password"
                type="password"
                className="w-full px-3.5 py-2.5 border border-border-emphasis rounded-sm text-[0.875rem] pointer-coarse:text-base font-sans bg-surface-inset text-ink placeholder-ink-muted focus:outline-none focus:border-accent focus:shadow-[0_0_0_3px_rgba(200,150,62,0.1)] transition-[border-color,box-shadow] duration-150"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
              <div className="flex justify-end mt-1.5">
                <Link
                  to="/forgot-password"
                  className="text-[0.75rem] text-ink-muted hover:text-ink-secondary transition-colors duration-150 no-underline"
                >
                  ¿Olvidaste tu contraseña?
                </Link>
              </div>
            </div>

            {error && (
              <p className="text-destructive text-[0.8125rem] mb-4 bg-destructive-subtle px-3 py-2 rounded-sm border border-destructive/20">
                {error}
              </p>
            )}

            <button
              type="submit"
              className="w-full px-6 py-2.5 bg-accent text-ink-inverse border-none rounded-sm text-[0.8125rem] font-semibold font-sans inline-flex items-center justify-center gap-2 cursor-pointer transition-[background-color,opacity] duration-150 hover:bg-accent-hover disabled:opacity-40 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? 'Verificando...' : 'Ingresar'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
